import { customElement } from 'lit/decorators.js'
import { html, LitElement, nothing, type TemplateResult } from 'lit'
import { style } from './delivery-match-method-group.style.js'
import { consume } from '@lit/context'
import { DeliveryMatchSelectedMethodGroupContext, type DeliveryMatchSelectedMethodGroupContextType, type DeliveryMethodGroupType } from './provider.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole, NO_ICON_CONFIGURED } from '@cloudsuite/mosaic-shared/icons/library.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'

type ICSCDeliveryMatchMethodGroup = {
  type: DeliveryMethodGroupType
  caption: string | TemplateResult | null
  showRadioButton?: boolean
  showIcon?: boolean
}

initializeIconsByRole({
  'csc-delivery-match-method-group.delivery': { name: 'truck', style: 'regular' },
  'csc-delivery-match-method-group.dropoff': { name: 'shop', style: 'regular' },
  'csc-delivery-match-method-group.delivery-caption': NO_ICON_CONFIGURED,
  'csc-delivery-match-method-group.dropoff-caption': NO_ICON_CONFIGURED,
})

@customElement('csc-delivery-match-method-group')
export class CSCDeliveryMatchMethodGroup extends LitElement {
  type: ICSCDeliveryMatchMethodGroup['type']
  caption: ICSCDeliveryMatchMethodGroup['caption']
  showRadioButton: ICSCDeliveryMatchMethodGroup['showRadioButton'] = false
  showIcon: ICSCDeliveryMatchMethodGroup['showIcon'] = false

  static override styles = [hostStyle, style]

  @consume({ context: DeliveryMatchSelectedMethodGroupContext, subscribe: true })
  selectedMethodGroup!: DeliveryMatchSelectedMethodGroupContextType

  constructor({ type, caption, showRadioButton, showIcon }: ICSCDeliveryMatchMethodGroup) {
    super()
    this.type = type
    this.caption = caption
    this.showRadioButton = showRadioButton
    this.showIcon = showIcon
  }

  renderCaption = () => {
    if (!this.caption) return nothing
    return html`<cse-text class="caption caption-${this.type}">
      <cse-icon icon-role="csc-delivery-match-method-group.${this.type}-caption"></cse-icon>
      ${this.caption}
    </cse-text>`
  }

  renderIcon = () => {
    if (this.showIcon) {
      return html` <cse-icon icon-role="csc-delivery-match-method-group.${this.type}"></cse-icon>`
    }

    return nothing
  }

  renderTitle = () => {
    if (this.type === 'delivery') {
      return html`${t('method_group_delivery', { ns: 'csc-delivery-match', default: 'Delivery' })}`
    } else {
      return html`${t('method_group_dropoff', { ns: 'csc-delivery-match', default: 'Pickup' })}`
    }
  }

  renderContent = () => {
    return html` <div class="content">
      <cse-text class="group-name" variant="heading-5">
        ${this.renderIcon()} ${this.renderTitle()}
      </cse-text>
      ${this.renderCaption()}
    </div>`
  }

  override render() {
    const selected = this.selectedMethodGroup === this.type

    return html`<div class="wrapper">
      ${this.showRadioButton
        ? html`<cse-radio-button .checked="${selected}" id="delivery-match-type-${this.type}" value="${this.type}">
            ${this.renderContent()}
          </cse-radio-button>`
        : this.renderContent()}
    </div>`
  }
}
